import axios from 'axios';

import createUrlWithParams from 'helpers/createUrlWithParams';

import {
  parseActiveRopaReviewResponseForFE,
  parseAcceptProcessingReviewForBE,
  parseAdminAssetProcessingActivityReviewDetailForFE,
  parseAssetProcessingActivityReviewActionForFE,
  parseAssetRopaReviewListParamsForBE,
  parseAssetRopaReviewsForFE,
  parseRopaReviewCancelForBE,
  parseRopaReviewCompleteForBE,
  parseRopaReviewCreateForBE,
  parseRopaReviewDetailsUpdateForBE,
  parseCancelIncompleteAssetReviewForBE,
  parseRopaReviewRoundForFE,
  parseRopaReviewStartSummaryForFE,
  parseRequestClarificationForBE,
} from './ropaReviews.parsers';
import type {
  ActiveRopaReviewResponseDTO,
  AdminAssetProcessingActivityReviewDetailDTO,
  AdminAssetRopaReviewListDTO,
  AssetProcessingActivityReviewActionOutputDTO,
  AssetRopaReviewListParamsInput,
  BulkAcceptSubmittedOutputDTO,
  AcceptProcessingReviewInput,
  CancelIncompleteAssetReviewInput,
  RequestClarificationInput,
  RopaReviewCancelInput,
  RopaReviewCompleteInput,
  RopaReviewCreateInput,
  RopaReviewDetailsUpdateInput,
  RopaReviewRoundOutputDTO,
  RopaReviewsApi,
  RopaReviewStartSummaryDTO,
} from './ropaReviews.types';

const buildBaseEndpoint = (organizationId: number) => `api/v1/organizations/${organizationId}/ropa-reviews/`;

const buildReviewEndpoint = (organizationId: number, reviewRoundId: number) => `${buildBaseEndpoint(organizationId)}${reviewRoundId}/`;

const buildActiveEndpoint = (organizationId: number) => `${buildBaseEndpoint(organizationId)}active/`;

const buildStartSummaryEndpoint = (organizationId: number) => `${buildBaseEndpoint(organizationId)}start-summary/`;

const buildAssetReviewsEndpoint = (organizationId: number, reviewRoundId: number) =>
  `${buildReviewEndpoint(organizationId, reviewRoundId)}assets/`;

const buildBulkAcceptEndpoint = (organizationId: number, reviewRoundId: number, assetReviewId: number) =>
  `${buildReviewEndpoint(organizationId, reviewRoundId)}asset-reviews/${assetReviewId}/accept-submitted/`;

const buildCompleteReviewEndpoint = (organizationId: number, reviewRoundId: number) =>
  `${buildReviewEndpoint(organizationId, reviewRoundId)}complete/`;

const buildCancelReviewEndpoint = (organizationId: number, reviewRoundId: number) =>
  `${buildReviewEndpoint(organizationId, reviewRoundId)}cancel/`;

const buildCancelIncompleteAssetReviewEndpoint = (organizationId: number, reviewRoundId: number, assetReviewId: number) =>
  `${buildReviewEndpoint(organizationId, reviewRoundId)}asset-reviews/${assetReviewId}/cancel/`;

const buildAssetProcessingActivityReviewEndpoint = (organizationId: number, reviewId: number) =>
  `api/v1/organizations/${organizationId}/asset-processing-activity-reviews/${reviewId}/`;

const buildAcceptAssetProcessingActivityReviewEndpoint = (organizationId: number, reviewId: number) =>
  `${buildAssetProcessingActivityReviewEndpoint(organizationId, reviewId)}accept/`;

const buildRequestAssetProcessingActivityReviewClarificationEndpoint = (organizationId: number, reviewId: number) =>
  `${buildAssetProcessingActivityReviewEndpoint(organizationId, reviewId)}request-clarification/`;

const parseAssetReviewsResponse = (data: unknown) => {
  if (Array.isArray(data)) {
    return parseAssetRopaReviewsForFE(data as AdminAssetRopaReviewListDTO[]);
  }

  if (
    data &&
    typeof data === 'object' &&
    'results' in data &&
    Array.isArray((data as { results: AdminAssetRopaReviewListDTO[] }).results)
  ) {
    return parseAssetRopaReviewsForFE((data as { results: AdminAssetRopaReviewListDTO[] }).results);
  }

  if (data && typeof data === 'object' && 'id' in data && 'asset_name' in data) {
    return parseAssetRopaReviewsForFE([data as AdminAssetRopaReviewListDTO]);
  }

  return [];
};

export const ropaReviewsApi: RopaReviewsApi = {
  getStartSummary: async organizationId => {
    const { data } = await axios.get(buildStartSummaryEndpoint(organizationId));
    return { data: parseRopaReviewStartSummaryForFE(data as RopaReviewStartSummaryDTO) };
  },
  createReview: async (organizationId, payload: RopaReviewCreateInput) => {
    const { data } = await axios.post(buildBaseEndpoint(organizationId), parseRopaReviewCreateForBE(payload));
    return { data: parseRopaReviewRoundForFE(data as RopaReviewRoundOutputDTO) };
  },
  getActiveReview: async organizationId => {
    try {
      const { data } = await axios.get(buildActiveEndpoint(organizationId));
      return { data: parseActiveRopaReviewResponseForFE(data as ActiveRopaReviewResponseDTO) };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return { data: null };
      }

      throw error;
    }
  },
  updateReviewDetails: async (organizationId, reviewRoundId, payload: RopaReviewDetailsUpdateInput) => {
    const { data } = await axios.patch(buildReviewEndpoint(organizationId, reviewRoundId), parseRopaReviewDetailsUpdateForBE(payload));
    return { data: parseActiveRopaReviewResponseForFE(data as ActiveRopaReviewResponseDTO) };
  },
  getAssetReviews: async (organizationId, reviewRoundId, params?: AssetRopaReviewListParamsInput) => {
    const { data } = await axios.get(
      createUrlWithParams(buildAssetReviewsEndpoint(organizationId, reviewRoundId), parseAssetRopaReviewListParamsForBE(params)),
    );
    return { data: parseAssetReviewsResponse(data) };
  },
  /** @deprecated Use acceptAssetProcessingActivityReview */
  acceptProcessingReview: async (organizationId, _reviewRoundId, reviewRowId, payload: AcceptProcessingReviewInput = {}) =>
    ropaReviewsApi.acceptAssetProcessingActivityReview(organizationId, reviewRowId, payload),
  /** @deprecated Use requestAssetProcessingActivityReviewClarification */
  requestClarification: async (organizationId, _reviewRoundId, reviewRowId, payload: RequestClarificationInput) =>
    ropaReviewsApi.requestAssetProcessingActivityReviewClarification(organizationId, reviewRowId, payload),
  getAssetProcessingActivityReviewDetail: async (organizationId, reviewId) => {
    const { data } = await axios.get(buildAssetProcessingActivityReviewEndpoint(organizationId, reviewId));
    return { data: parseAdminAssetProcessingActivityReviewDetailForFE(data as AdminAssetProcessingActivityReviewDetailDTO) };
  },
  acceptAssetProcessingActivityReview: async (organizationId, reviewId, payload: AcceptProcessingReviewInput = {}) => {
    const { data } = await axios.post(
      buildAcceptAssetProcessingActivityReviewEndpoint(organizationId, reviewId),
      parseAcceptProcessingReviewForBE(payload),
    );
    return { data: parseAssetProcessingActivityReviewActionForFE(data as AssetProcessingActivityReviewActionOutputDTO) };
  },
  requestAssetProcessingActivityReviewClarification: async (organizationId, reviewId, payload: RequestClarificationInput) => {
    const { data } = await axios.post(
      buildRequestAssetProcessingActivityReviewClarificationEndpoint(organizationId, reviewId),
      parseRequestClarificationForBE(payload),
    );
    return { data: parseAssetProcessingActivityReviewActionForFE(data as AssetProcessingActivityReviewActionOutputDTO) };
  },
  bulkAcceptSubmitted: async (organizationId, reviewRoundId, assetReviewId) => {
    const { data } = await axios.post(buildBulkAcceptEndpoint(organizationId, reviewRoundId, assetReviewId), {});
    return { data: data as BulkAcceptSubmittedOutputDTO };
  },
  completeReview: async (organizationId, reviewRoundId, payload: RopaReviewCompleteInput) => {
    const { data } = await axios.post(buildCompleteReviewEndpoint(organizationId, reviewRoundId), parseRopaReviewCompleteForBE(payload));
    return { data: parseActiveRopaReviewResponseForFE(data as ActiveRopaReviewResponseDTO) };
  },
  cancelReview: async (organizationId, reviewRoundId, payload: RopaReviewCancelInput) => {
    const { data } = await axios.post(buildCancelReviewEndpoint(organizationId, reviewRoundId), parseRopaReviewCancelForBE(payload));
    return { data: parseActiveRopaReviewResponseForFE(data as ActiveRopaReviewResponseDTO) };
  },
  cancelIncompleteAssetReview: async (organizationId, reviewRoundId, assetReviewId, payload: CancelIncompleteAssetReviewInput) => {
    await axios.post(
      buildCancelIncompleteAssetReviewEndpoint(organizationId, reviewRoundId, assetReviewId),
      parseCancelIncompleteAssetReviewForBE(payload),
    );
  },
  getProcessingReviewDetail: async (organizationId, reviewId) =>
    ropaReviewsApi.getAssetProcessingActivityReviewDetail(organizationId, reviewId),
};

export default ropaReviewsApi;
